
/**
 * Module dependencies.
 */

import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormControl, FormControlWrapper, FormGroup, Label } from '../../forms/fields-styled-components';
import { KeyboardEvent, ReactNode, useRef, useState } from 'react';
import { Tag, addTag, removeLastTag, removeTag } from '../../tag';
import { ifProp } from 'styled-tools';
import { isEmpty, map, trim } from 'lodash';
import { media } from 'react-components/styles';
import { useField, useFormState } from '@seegno/react-forms';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  'aria-describedby'?: string;
  className?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  isAcceptEvent?: (event: KeyboardEvent<HTMLInputElement>) => boolean;
  label: string;
  name: string;
  normalizeItem?: (item: string) => string;
};

/**
 * `StyledFormControlWrapper` styled component.
 */

const StyledFormControlWrapper = styled(FormControlWrapper)`
  flex-wrap: wrap;
  gap: 8px;
  padding: 20px 10px 6px;

  ${media.min('md')`
    padding: 22px 14px 6px;
  `}
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label)<{ isFloating: boolean }>`
  top: 15px;

  ${ifProp('isFloating', css`
    transform: translateY(-12px);
  `)}

  ${media.min('md')`
    top: 18px;

    ${ifProp('isFloating', css`
      transform: translateY(-14px);
    `)}
  `}
`;

/**
 * `StyledFormControl` styled component.
 */

const StyledFormControl = styled(FormControl)`
  min-height: 28px;
  padding: 0;
  width: auto;

  ${media.min('md')`
    min-height: 30px;
    padding: 0;
  `}
`;

/**
 * Default accept event.
 */

const defaultAcceptEvent = ({ key }: { key: string }) => key === 'Enter';

/**
 * Export `TagsInput` component.
 */

export function TagsInput(props: Props): JSX.Element {
  const {
    className,
    disabled,
    helpText,
    isAcceptEvent = defaultAcceptEvent,
    label,
    name,
    normalizeItem = trim,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [wasFilled, setWasFilled] = useState(false);
  const [selectingAutocomplete, setSelectingAutocomplete] = useState(false);
  const { error, meta, onBlur, onChange, onFocus, value } = useField(name);
  const { active } = meta ?? {};
  const { alreadySubmitted, isSubmitting } = useFormState();
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;

  function appendValue(event: any) {
    const newValue = normalizeItem(event.target.value);

    if (!isEmpty(newValue)) {
      addTag(value, newValue, newTags => {
        onChange(newTags);
        setInputValue('');
      });
    }
  }

  return (
    <FormGroup
      className={className}
      disabled={isDisabled}
      hasAlert={hasError || !!helpText}
      onClick={() => inputRef.current && inputRef.current.focus()}
    >
      <StyledFormControlWrapper
        disabled={isDisabled}
        hasError={hasError}
        hasFocus={active}
      >
        <StyledLabel
          disabled={isDisabled}
          htmlFor={name}
          isFloating={Boolean(value) || active}
        >
          {label}
        </StyledLabel>

        {map(value, (tag, index: number) => (
          <Tag
            disabled={isDisabled}
            key={tag}
            name={`${name}[${index}]`}
            onRemove={() => onChange(removeTag(value, tag))}
            value={tag}
          />
        ))}

        <StyledFormControl
          {...rest}
          disabled={isDisabled}
          id={name}
          name={name}
          onBlur={event => {
            appendValue(event);
            onBlur(event);
          }}
          onChange={event => {
            const newValue = event.target.value;

            if (isEmpty(newValue) && !isEmpty(inputValue)) {
              setWasFilled(true);
            }

            setInputValue(newValue);
          }}
          onFocus={onFocus}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              // Prevent form submit unless the user is pressing ctrl or meta.
              if (event.ctrlKey || event.metaKey) {
                return;
              }

              event.preventDefault();
            }
          }}
          onKeyUp={event => {
            if (event.key === 'Enter' && selectingAutocomplete) {
              return setSelectingAutocomplete(false);
            }

            if (isAcceptEvent(event)) {
              return appendValue(event);
            }

            switch (event.key) {
              case 'Backspace': {
                // Don't remove last tag on backspace event that cleared the input.
                if (wasFilled) {
                  setWasFilled(false);
                } else if (isEmpty(inputValue)) {
                  onChange(removeLastTag(value));
                }

                return;
              }

              case 'ArrowDown':
              case 'ArrowUp':
              case 'Down':
              case 'Up':
                return setSelectingAutocomplete(true);

              default: {
                setWasFilled(false);
                setSelectingAutocomplete(false);
              }
            }
          }}
          ref={inputRef}
          type={'text'}
          value={inputValue}
        />
      </StyledFormControlWrapper>

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </FormGroup>
  );
}
